import { v4 as uuidv4 } from 'uuid';

// This function is looking for { chapters: ... } at the top level
function rehydrateContractClausesInternal(
  serializedData,
  clausesById = {},
  level = 0,
) {
  let rootClauseId = 'CLAUSE_ROOT';
  if (level > 0) {
    if (
      typeof serializedData?.id === 'string' &&
      serializedData?.id &&
      !clausesById[serializedData.id] // deal with duplicate IDs
    ) {
      rootClauseId = serializedData.id;
    } else {
      // Note: if there’s no valid ID we make a new one – it’ll get
      // properly burnt in to the draft on the next save. It does
      // mean all the components will be recreated each time the
      // server data changes, but that’s not a big deal, actually
      rootClauseId = uuidv4();
    }
  }
  const clause = {
    showPreamble: false,
    title: null,
    // titleHidden not used for chapters
    titleHidden: level > 1,
    body: null,
    children: [],
  };
  clausesById[rootClauseId] = clause;

  if (level > 0) {
    const serializedTitle = serializedData?.title;
    if (typeof serializedTitle === 'string') {
      clause.titleHidden = false;
      clause.title = {
        text: serializedTitle,
      };
    }
    const serializedBody =
      level > 1 ? serializedData?.body : serializedData?.preamble;
    if (typeof serializedBody === 'string') {
      clause.showPreamble = level === 1;
      clause.body = {
        text: serializedBody,
      };
    }
  }

  let serializedChildren = serializedData?.children;
  if (level === 0) {
    serializedChildren = serializedData?.chapters;
  } else if (level === 1) {
    serializedChildren = serializedData?.articles;
  }
  if (!Array.isArray(serializedChildren)) {
    serializedChildren = [];
  }
  for (const serializedChildClause of serializedChildren) {
    if (typeof serializedChildClause !== 'object') {
      continue;
    }
    const { rootClauseId: childClauseId } =
      rehydrateContractClausesInternal(
        serializedChildClause,
        clausesById,
        level + 1,
      ) || {};
    if (childClauseId) {
      clause.children.push(childClauseId);
    }
  }

  const clauses = {
    rootClauseId,
    clausesById,
  };
  // if (level === 0) {
  //   console.debug({
  //     rehydrateContractClauses: clauses,
  //     fromData: serializedData,
  //   });
  // }
  return clauses;
}

export function rehydrateContractClauses(serializedChapters) {
  return rehydrateContractClausesInternal({ chapters: serializedChapters });
}

export function serializeContractClauses(
  strippedDocument,
  fromClauseId,
  level = 0,
) {
  const clauseId = fromClauseId || strippedDocument.rootClauseId;
  const clause = (clauseId && strippedDocument.clausesById[clauseId]) || {};
  const formattedClause = {};
  if (level > 0) {
    formattedClause.id = clauseId;
    const titleText = clause.title?.text;
    if (!clause.titleHidden && titleText) {
      formattedClause.Title = titleText;
    }
    const bodyText = clause.body?.text;
    if (bodyText) {
      if (level === 1) {
        formattedClause.Preamble = bodyText;
      } else {
        formattedClause.Body = bodyText;
      }
    }
  }
  const children = (clause.children || [])
    .map((childClauseId) =>
      serializeContractClauses(strippedDocument, childClauseId, level + 1),
    )
    .filter((childClause) => childClause);
  if (children.length) {
    switch (level) {
      case 0:
        formattedClause.Chapters = children;
        break;
      case 1:
        formattedClause.Articles = children;
        break;
      default:
        formattedClause.Children = children;
    }
  }
  // if (level === 0) {
  //   console.debug({
  //     serializeContractClauses: formattedClause,
  //     fromData: strippedDocument,
  //   });
  // }
  return formattedClause;
}

export function rehydrateContractDefinitions(serializedDefinitions) {
  const rootClauseId = 'DEFS_ROOT';
  const definitionIds = [];
  const clausesById = {
    [rootClauseId]: {
      title: null,
      titleHidden: true,
      body: null,
      children: definitionIds,
    },
  };

  if (!Array.isArray(serializedDefinitions)) {
    serializedDefinitions = [];
  }
  for (const serializedDefinition of serializedDefinitions) {
    if (typeof serializedDefinition !== 'object') {
      continue;
    }

    let clauseId = serializedDefinition.id;
    if (
      typeof clauseId !== 'string' ||
      !clauseId ||
      clausesById[clauseId] // deal with duplicate IDs
    ) {
      // Note: if there’s no valid ID we make a new one – it’ll get
      // properly burnt in to the draft on the next save. It does
      // mean all the components will be recreated each time the
      // server data changes, but that’s not a big deal, actually
      clauseId = uuidv4();
    }
    const clause = {
      title: null,
      titleHidden: false,
      body: null,
      children: [],
    };
    if (typeof serializedDefinition.term === 'string') {
      clause.title = {
        text: serializedDefinition.term,
      };
    }
    if (typeof serializedDefinition.definition === 'string') {
      clause.body = {
        text: serializedDefinition.definition,
      };
    }
    definitionIds.push(clauseId);
    clausesById[clauseId] = clause;
  }

  const definitions = {
    rootClauseId,
    clausesById,
  };
  // console.debug({
  //   rehydrateContractDefinitions: definitions,
  //   fromData: serializedDefinitions,
  // });
  return definitions;
}

export function serializeContractDefinitions({
  rootClauseId,
  clausesById = {},
}) {
  const definitionIds = clausesById[rootClauseId]?.children || [];
  const serializedDefinitions = {
    Definitions: definitionIds
      .map((clauseId) => {
        const clause = clausesById[clauseId];
        if (!clause) {
          return null;
        }
        const definition = {
          id: clauseId,
        };
        if (clause.title?.text) {
          definition.Term = clause.title.text;
        }
        if (clause.body?.text) {
          definition.Definition = clause.body.text;
        }
        return definition;
      })
      .filter((definition) => definition),
  };
  // console.debug({
  //   serializeContractDefinitions: serializedDefinitions,
  //   fromData: {
  //     rootClauseId,
  //     clausesById,
  //   },
  // });
  return serializedDefinitions;
}
